/** Gemeinsame Typen fuer Server und Browser. */

export type Role = 'admin' | 'member';

/* ------------------------------------------------------------------------ *
 * Rechte
 *
 * Vorher galt: Admin sieht alles, Mitglied nichts von den Kosten. Dazwischen
 * gab es nichts - und genau dort liegen die realen Faelle. Die Buchhaltung
 * braucht Preise, aber keine Teamverwaltung. Eine Projektleitung braucht
 * vielleicht Vertragskosten, aber sicher keine Gehaelter.
 *
 * Deshalb einzelne Rechte statt weiterer Rollen. Ein Admin hat immer alle -
 * sonst koennte sich niemand mehr aus einer verfahrenen Lage befreien.
 * ------------------------------------------------------------------------ */

export interface Permissions {
  /**
   * Alle Zugaenge sehen und verwalten - auch die, mit denen man nichts zu tun
   * hat.
   *
   * Ohne dieses Recht sieht man ausschliesslich, was einem freigegeben wurde
   * oder wo man einen Platz hat. Das ist die Voreinstellung: wer bei
   * Spesenfuchs sein Konto hat, muss deshalb nicht die Serverzugaenge kennen.
   */
  viewAllItems: boolean;
  /** Preise, Steuer, Zahlungsmittel, Abbuchungstage, Rechnungswege. */
  viewCosts: boolean;
  /** Diese Angaben auch aendern. Schliesst das Sehen mit ein. */
  manageCosts: boolean;
  /** Sonstige laufende Kosten - dort stehen die Gehaelter. */
  viewPayroll: boolean;
  /** Rollen und Rechte vergeben, Konten sperren, Ein- und Austritte fuehren. */
  manageTeam: boolean;
  /** Das Protokoll einsehen. */
  viewAudit: boolean;
}

export type PermissionKey = keyof Permissions;

export const PERMISSION_KEYS: PermissionKey[] = [
  'viewAllItems',
  'viewCosts',
  'manageCosts',
  'viewPayroll',
  'manageTeam',
  'viewAudit',
];

export const PERMISSION_LABEL: Record<PermissionKey, string> = {
  viewAllItems: 'Alle Zugänge sehen und verwalten',
  viewCosts: 'Preise sehen',
  manageCosts: 'Preise bearbeiten',
  viewPayroll: 'Sonstige Kosten sehen',
  manageTeam: 'Team verwalten',
  viewAudit: 'Protokoll einsehen',
};

export const PERMISSION_HINT: Record<PermissionKey, string> = {
  viewAllItems:
    'Auch Zugänge sehen und bearbeiten, die dieser Person nicht freigegeben sind. Passwörter bleiben trotzdem verschlossen – dafür braucht es weiterhin eine Freigabe.',
  viewCosts:
    'Preise, Steuer, Zahlungsmittel und Rechnungswege der Zugänge. Ohne dieses Recht bleiben alle Beträge verborgen – auch in der Ausgabe als Tabelle.',
  manageCosts: 'Preise und Verträge ändern. Schließt das Sehen mit ein.',
  viewPayroll:
    'Löhne, Steuern, Versicherungen, Miete. Hier stehen die Gehälter – bewusst getrennt vom Recht auf Vertragspreise.',
  manageTeam: 'Rollen und Rechte vergeben, Konten sperren, Ein- und Austritte führen.',
  viewAudit: 'Wer wann was getan hat.',
};

export const NO_PERMISSIONS: Permissions = {
  viewAllItems: false,
  viewCosts: false,
  manageCosts: false,
  viewPayroll: false,
  manageTeam: false,
  viewAudit: false,
};

export const ALL_PERMISSIONS: Permissions = {
  viewAllItems: true,
  viewCosts: true,
  manageCosts: true,
  viewPayroll: true,
  manageTeam: true,
  viewAudit: true,
};

/**
 * Was jemand tatsaechlich darf. Administratoren duerfen alles, und wer
 * bearbeiten darf, darf zwangslaeufig auch sehen - sonst koennte man Preise
 * blind ueberschreiben.
 */
export function effectivePermissions(
  role: Role,
  granted?: Partial<Permissions> | null,
): Permissions {
  if (role === 'admin') return { ...ALL_PERMISSIONS };
  const p = { ...NO_PERMISSIONS, ...(granted ?? {}) };
  if (p.manageCosts) p.viewCosts = true;
  // Preise ohne die zugehoerigen Zugaenge waeren eine Liste von Betraegen ohne
  // Gegenstand - Prognose und Rechnungsuebersicht bauen auf beidem auf.
  if (p.viewCosts) p.viewAllItems = true;
  return p;
}
export type UserStatus = 'active' | 'disabled';

/** Nutzerdaten, wie sie an den Browser gehen. Enthaelt nie den privaten Schluessel im Klartext. */
export interface PublicUser {
  uid: string;
  email: string;
  name: string;
  picture: string | null;
  role: Role;
  status: UserStatus;
  /** true, sobald der Nutzer eine Master-Passphrase gesetzt hat. */
  hasVaultKey: boolean;
  /** base64(SPKI) des RSA-OAEP-Public-Keys. Noetig, um ihm Items freizugeben. */
  publicKey: string | null;
  createdAt: string;
  lastSeenAt: string | null;
  /** Gesetzt, wenn der Nutzer seinen Schluessel zuruecksetzen musste und Zugaenge neu braucht. */
  needsRegrant: boolean;
  /** Zurueckgestellte Hinweise auf der Uebersicht. Nur in der eigenen Sitzung gefuellt. */
  dismissedHints: DismissedHints;
  /** Was diese Person darf - bei Administratoren immer alles. */
  permissions: Permissions;
}

/** Der verschluesselte private Schluessel eines Nutzers, plus KDF-Parameter. */
export interface VaultKeyBundle {
  publicKey: string;
  encryptedPrivateKey: EncryptedBlob;
  kdfSalt: string;
  kdfMemoryKiB: number;
  kdfIterations: number;
  kdfParallelism: number;
}

export interface EncryptedBlob {
  /** base64, 12 Byte */
  iv: string;
  /** base64, AES-256-GCM Ciphertext inkl. Auth-Tag */
  data: string;
}

export type BillingCycle =
  | 'monthly'
  | 'quarterly'
  | 'yearly'
  | 'weekly'
  | 'onetime'
  | 'free';

export type ItemStatus = 'active' | 'trial' | 'cancelled';

/** Woher die Rechnungen kommen. */
export type InvoicePortal = 'none' | 'website' | 'lexoffice' | 'getmyinvoices' | 'email' | 'post';

export const PORTAL_LABEL: Record<InvoicePortal, string> = {
  none: 'Nicht hinterlegt',
  website: 'Beim Anbieter herunterladen',
  lexoffice: 'lexoffice',
  getmyinvoices: 'GetMyInvoices',
  email: 'Kommt per E-Mail',
  post: 'Kommt per Post',
};

/**
 * Sinnbilder fuer Zugaenge ohne Website - WLAN, Server, Alarmanlage. Bewusst
 * eine feste, kleine Auswahl statt freier Bilder: sie sind Teil der
 * Oberflaeche, laden nichts nach und funktionieren auch im Dunkelmodus.
 */
export const ICON_SYMBOLS = [
  'wlan',
  'server',
  'netzwerk',
  'schloss',
  'gebaeude',
  'drucker',
  'kamera',
  'musik',
  'cloud',
  'karte',
  'werkzeug',
  'stern',
] as const;

export type IconSymbol = (typeof ICON_SYMBOLS)[number];

export const ICON_LABEL: Record<IconSymbol, string> = {
  wlan: 'WLAN',
  server: 'Server',
  netzwerk: 'Netzwerk',
  schloss: 'Schloss',
  gebaeude: 'Gebäude',
  drucker: 'Drucker',
  kamera: 'Kamera',
  musik: 'Musik',
  cloud: 'Cloud',
  karte: 'Zahlung',
  werkzeug: 'Werkzeug',
  stern: 'Sonstiges',
};

/**
 * Vorschlaege fuer die Kategorie. Bewusst nur Vorschlaege und keine feste
 * Liste - jedes Team hat Dinge, an die hier niemand gedacht hat.
 */
export const CATEGORY_SUGGESTIONS = [
  'Netzwerk & WLAN',
  'Server & Hosting',
  'Gebäude & Geräte',
  'Online-Shop',
  'Versand & Logistik',
  'Design & Kreativ',
  'KI & Automatisierung',
  'Stock & Lizenzen',
  'Musik & Audio',
  'Hosting & Domains',
  'Kommunikation',
  'Projektmanagement',
  'Buchhaltung & Steuern',
  'Bank & Zahlung',
  'Social Media',
  'Hardware & Geräte',
  'Behörden & Versicherung',
  'Sonstiges',
];

/** Womit bezahlt wird. Strukturiert, damit sich Kosten je Karte summieren lassen. */
export type PaymentMethod =
  | 'unknown'
  | 'amex'
  | 'mastercard'
  | 'visa'
  | 'paypal'
  | 'sepa'
  | 'invoice'
  | 'other';

export const PAYMENT_LABEL: Record<PaymentMethod, string> = {
  unknown: 'Nicht hinterlegt',
  amex: 'American Express',
  mastercard: 'Mastercard',
  visa: 'Visa',
  paypal: 'PayPal',
  sepa: 'Lastschrift (SEPA)',
  invoice: 'Rechnung',
  other: 'Sonstiges',
};

/**
 * Drei grundverschiedene Arten von Eintraegen. Der Unterschied zeigt sich vor
 * allem beim Austritt: ein geteiltes Passwort muss gewechselt werden, ein
 * persoenlicher Platz wird beim Anbieter entfernt, eine gekaufte Lizenz wird
 * deaktiviert und neu vergeben.
 */
export type ItemKind =
  /** Ein Login, das sich alle teilen. Higgsfield, Artlist, der Twitter-Account. */
  | 'shared'
  /** Ein Vertrag mit Plaetzen, jede Person hat ihren eigenen Login. Adobe, Slack. */
  | 'seats'
  /** Einmal gekaufte Software mit Seriennummern. DaVinci Resolve, Plugins. */
  | 'license'
  /** WLAN, Server, Netzwerkgeraet, Alarmanlage. Kein Anbieter, kein Vertrag. */
  | 'local';

export const KIND_LABEL: Record<ItemKind, string> = {
  shared: 'Geteilter Zugang',
  seats: 'Persönliche Plätze',
  license: 'Gekaufte Lizenz',
  local: 'Lokaler Zugang',
};

export const KIND_HINT: Record<ItemKind, string> = {
  shared: 'Ein Passwort, das sich alle teilen.',
  seats: 'Ein Vertrag, aber jede Person hat einen eigenen Login.',
  license: 'Einmal gekauft, mit Seriennummern statt Abo.',
  local: 'WLAN, Server, Netzwerkgerät – kein Anbieter, kein Vertrag.',
};

/**
 * Ein Platz innerhalb eines Eintrags: ein Adobe-Zugang, eine Seriennummer,
 * eine Lizenz auf einem bestimmten Rechner.
 *
 * Das eigene Geheimnis ist optional. Bei Adobe reicht oft die Information
 * *wer* einen Platz hat - das persoenliche Passwort behaelt jede Person fuer
 * sich. Bei DaVinci ist die Seriennummer dagegen genau das, was hier hingehoert.
 */
export type LoginMethod = 'unknown' | 'email' | 'google' | 'microsoft' | 'apple' | 'sso' | 'magic';

export const LOGIN_METHOD_LABEL: Record<LoginMethod, string> = {
  unknown: 'nicht angegeben',
  email: 'Eigene Zugangsdaten',
  google: 'Mit Google anmelden',
  microsoft: 'Mit Microsoft anmelden',
  apple: 'Mit Apple anmelden',
  sso: 'Firmen-Anmeldung (SSO)',
  magic: 'Link per E-Mail',
};

export const LOGIN_METHOD_HINT: Record<LoginMethod, string> = {
  unknown: '',
  email: 'E-Mail-Adresse und ein eigenes Passwort, hier hinterlegt.',
  google: 'Über den Google-Knopf mit der wach.studio-Adresse. Es gibt kein eigenes Passwort.',
  microsoft: 'Über das Microsoft-Konto. Es gibt kein eigenes Passwort.',
  apple: 'Über die Apple-ID. Es gibt kein eigenes Passwort.',
  sso: 'Über die zentrale Firmen-Anmeldung. Es gibt kein eigenes Passwort.',
  magic: 'Anmeldelink kommt per E-Mail, es gibt kein festes Passwort.',
};

/** Ob bei dieser Anmeldeart ueberhaupt ein Passwort existiert. */
export function hasOwnPassword(method: LoginMethod): boolean {
  return method === 'unknown' || method === 'email';
}

export interface Assignment {
  id: string;
  /** "Platz 1", "Seriennummer A", "Lizenz iMac Studio" */
  label: string;
  /**
   * Wer diesen Platz bzw. diese Lizenz nutzen darf. Leer heißt: bezahlt, aber
   * niemandem zugewiesen. Eine Seriennummer berechtigt oft mehrere Personen -
   * deshalb eine Liste und nicht eine einzelne Person.
   */
  assigneeUids: string[];
  /**
   * Wie viele Personen diese eine Lizenz laut Vertrag nutzen dürfen.
   * null heißt: unbegrenzt oder nicht bekannt.
   */
  maxUsers: number | null;
  /**
   * true: das ganze Team darf sie nutzen und das Geheimnis sehen. Praktisch bei
   * Werkzeugen, die ohnehin jede und jeder braucht.
   */
  openToAll: boolean;
  /** Bei Lizenzen: auf welchem Gerät aktiviert. */
  device: string;
  /**
   * Wie man sich bei diesem Platz anmeldet.
   *
   * Bei einem Vertrag mit mehreren Zugaengen ist das die haeufigste Rueckfrage:
   * "Nehme ich den Google-Knopf oder habe ich hier eigene Zugangsdaten?" Steht
   * es am Platz, muss niemand danach fragen - und es erklaert nebenbei, warum
   * bei manchen Plaetzen gar kein Passwort hinterlegt ist.
   */
  loginMethod: LoginMethod;
  /** Verschlüsseltes Eigengeheimnis, oder null wenn keins hinterlegt ist. */
  secret: EncryptedBlob | null;
  /** Wer dieses Geheimnis entschlüsseln darf. */
  accessUids: string[];
  createdAt: string;
  updatedAt: string;
}

/** Mehr Zuweisungen als die Lizenz erlaubt? Das ist ein Vertragsverstoß. */
export function isOverAssigned(assignment: Pick<Assignment, 'assigneeUids' | 'maxUsers' | 'openToAll'>): boolean {
  if (assignment.openToAll || assignment.maxUsers === null) return false;
  return assignment.assigneeUids.length > assignment.maxUsers;
}

/** Zuweisung plus dem für den anfragenden Nutzer verpackten Schlüssel. */
export interface AssignmentForUser extends Omit<Assignment, 'secret'> {
  secret: EncryptedBlob | null;
  wrappedKey: string;
}
export type Criticality = 'low' | 'medium' | 'high';

/** Offen gespeicherte Metadaten. Bewusst unverschluesselt, damit Summen und Filter moeglich sind. */
export interface ItemMeta {
  id: string;
  kind: ItemKind;
  title: string;
  url: string;
  username: string;
  category: string;
  tags: string[];

  status: ItemStatus;
  /**
   * Ob dieser Zugang ueberhaupt Geld kostet. Ein DHL-Konto oder der Login beim
   * Schreibwarenhaendler hat keinen Vertrag - dann sind alle Kostenfelder
   * bedeutungslos und werden ausgeblendet.
   */
  hasCost: boolean;
  cycle: BillingCycle;
  /** Preis pro Abrechnungszyklus in Cent. Ob je Platz oder gesamt, sagt `priceIsPerSeat`. */
  priceCents: number;
  currency: string;
  /** true, wenn `priceCents` je Platz gilt und mit `seats` multipliziert werden muss. */
  priceIsPerSeat: boolean;
  /** true: der eingetragene Preis ist netto. false: er ist brutto. */
  priceIsNet: boolean;
  /** Steuersatz in Prozent. 0 etwa bei Reverse-Charge-Rechnungen aus dem Ausland. */
  vatRatePercent: number;
  /** Wie viele Plätze bezahlt werden. Vergeben sind sie damit noch nicht. */
  seats: number | null;
  /** Bei gekaufter Software: wann angeschafft. */
  purchaseDate: string | null;
  /** ISO-Datum der naechsten Verlaengerung. */
  renewalDate: string | null;
  /** ISO-Datum, bis wann gekuendigt werden muss. */
  cancelBeforeDate: string | null;
  paymentMethod: PaymentMethod;
  /** Naeheres zum Zahlungsmittel: "AmEx ••1234", "buchhaltung@wach.studio". */
  paymentDetail: string;
  /**
   * Tag im Monat, an dem abgebucht wird (1-31). Bei jaehrlicher Abrechnung
   * sagt `renewalDate` das Datum, dieser Wert bleibt dann leer.
   */
  billingDay: number | null;
  /** Wer im Team ist fuer diesen Vertrag verantwortlich. */
  ownerUid: string | null;

  /**
   * Sinnbild aus der festen Auswahl, oder leer. Hat Vorrang vor `iconData` -
   * wer bewusst eines gewaehlt hat, will nicht das Favicon sehen.
   */
  iconSymbol: string;
  /**
   * Das Favicon des Anbieters als Bilddaten (data:-Adresse).
   *
   * Bewusst hier abgelegt und nicht bei jedem Aufruf von Google geholt: sonst
   * erfuehre ein Fremder bei jedem Seitenaufruf jeder Person, welche Dienste
   * das Team benutzt. Geholt wird es einmal, auf dem Server.
   */
  iconData: string;

  /** Woher die Rechnungen kommen. */
  invoicePortal: InvoicePortal;
  /** Direkter Link zum Rechnungsbereich beim Anbieter. */
  invoiceUrl: string;

  /**
   * Anleitung zu diesem Zugang - in der Regel ein awork-Dokument.
   *
   * Bewusst nur Titel und Adresse, nicht der Inhalt: der awork-Schluessel hat
   * Verwalterrechte, ein gespiegelter Text wuerde also die Rechte von awork
   * aushebeln. So oeffnet der Link awork, und awork entscheidet selbst, wer
   * mitlesen darf.
   */
  guideTitle: string;
  guideUrl: string;

  criticality: Criticality;

  /**
   * true: Neue Leute brauchen diesen Zugang. Ergibt beim Eintritt eine
   * Checkliste dessen, was fuer sie anzulegen oder freizugeben ist.
   */
  onboardingRequired: boolean;
  /**
   * true: Beim Austritt muss das Geheimnis gewechselt werden - unabhaengig von
   * der Art des Eintrags. Bei geteilten und lokalen Zugaengen ist das
   * voreingestellt, laesst sich aber auch fuer andere erzwingen.
   */
  rotateOnLeave: boolean;

  /** true, wenn das Passwort geaendert werden muss (z. B. nach einem Austritt). */
  needsRotation: boolean;
  rotationReason: string | null;
  lastRotatedAt: string | null;

  createdAt: string;
  updatedAt: string;
  updatedBy: string | null;
}

export interface Item extends ItemMeta {
  /**
   * Das Geheimnis auf Eintragsebene. Bei einem geteilten Zugang ist das *das*
   * Passwort. Bei Plaetzen und Lizenzen typischerweise der Verwaltungszugang
   * beim Anbieter - oder schlicht leer.
   */
  secret: EncryptedBlob;
  /** Wer den Eintrag verwaltet und sein Geheimnis lesen darf. */
  accessUids: string[];
  /** Die persoenlichen Zugaenge bzw. Lizenzen darunter. Leer bei geteilten Zugaengen. */
  assignments: Assignment[];
}

/** Item plus der fuer den anfragenden Nutzer verpackte Item-Key. */
export interface ItemForUser extends Omit<Item, 'assignments'> {
  wrappedKey: string;
  assignments: AssignmentForUser[];
}

/** Klartext-Inhalt von `secret`. Existiert nur im Browser. */
export interface SecretPayload {
  password: string;
  /** Seriennummer oder Lizenzschlüssel bei gekaufter Software. */
  licenseKey: string;
  notes: string;
  totpSecret: string;
  recoveryCodes: string;
  customFields: { label: string; value: string }[];
}

export const EMPTY_SECRET: SecretPayload = {
  password: '',
  licenseKey: '',
  notes: '',
  totpSecret: '',
  recoveryCodes: '',
  customFields: [],
};

/**
 * Was beim Anbieter tatsaechlich zu tun ist. Haengt an der Art des Eintrags,
 * nicht am Geschmack - ein Adobe-Platz wird nicht durch einen Passwortwechsel frei.
 */
export type OffboardingAction = 'password' | 'seat' | 'license' | 'local';

export const ACTION_LABEL: Record<OffboardingAction, string> = {
  password: 'Passwort ändern',
  seat: 'Platz entfernen',
  license: 'Lizenz freigeben',
  local: 'Schlüssel neu setzen',
};

export const ACTION_HINT: Record<OffboardingAction, string> = {
  password: 'Geteiltes Passwort beim Anbieter neu setzen und hier speichern.',
  seat: 'Den persönlichen Zugang beim Anbieter löschen oder den Platz neu vergeben.',
  license: 'Lizenz auf dem Gerät deaktivieren, dann einer anderen Person zuweisen.',
  local: 'Am Gerät selbst ändern und im Team neu verteilen – WLAN-Schlüssel, Server-Passwort.',
};

/** Was beim Eintritt fuer eine Person zu tun ist. */
export type OnboardingAction = 'grant' | 'create' | 'seat';

export const ONBOARDING_LABEL: Record<OnboardingAction, string> = {
  grant: 'Zugang freigeben',
  create: 'Konto anlegen',
  seat: 'Platz einrichten',
};

export const ONBOARDING_HINT: Record<OnboardingAction, string> = {
  grant: 'Das gemeinsame Passwort hier in LOCKER für die Person freischalten.',
  create: 'Beim Anbieter ein eigenes Konto anlegen, dann als Platz eintragen.',
  seat: 'Eine Lizenz zuweisen oder eine weitere beschaffen.',
};

export interface OnboardingEntry {
  itemId: string;
  title: string;
  url: string;
  category: string;
  criticality: Criticality;
  kind: ItemKind;
  action: OnboardingAction;
  /** Anleitung zum Zugang, falls hinterlegt - genau hier wird sie gebraucht. */
  guideTitle: string;
  guideUrl: string;
  /** Erledigt: Freigabe erteilt bzw. Platz zugewiesen - oder von Hand abgehakt. */
  done: boolean;
}

export interface OnboardingPlan {
  uid: string;
  email: string;
  name: string;
  startedAt: string;
  completedAt: string | null;
  entries: OnboardingEntry[];
}

/** Ein Eintrag auf der Offboarding-Checkliste. */
export interface OffboardingEntry {
  itemId: string;
  /** Gesetzt, wenn es um einen einzelnen Platz geht statt um den ganzen Eintrag. */
  assignmentId: string | null;
  title: string;
  /** Bezeichnung des Platzes, falls einer gemeint ist. */
  label: string;
  url: string;
  username: string;
  criticality: Criticality;
  category: string;
  monthlyCents: number;
  currency: string;
  action: OffboardingAction;
  /** Zugriff in LOCKER bereits entzogen? */
  accessRevoked: boolean;
  /** Die Handlung beim Anbieter ist erledigt. */
  done: boolean;
  /** War die Person hier verantwortlich? Dann muss der Vertrag uebergeben werden. */
  wasOwner: boolean;
}

export interface OffboardingPlan {
  uid: string;
  email: string;
  name: string;
  startedAt: string;
  completedAt: string | null;
  entries: OffboardingEntry[];
}

/**
 * Eine Kostenaenderung. Ohne diese Aufzeichnung liesse sich am Jahresende nicht
 * sagen, wo gespart wurde - ein geaenderter Preis ueberschreibt den alten
 * ersatzlos.
 */
export type CostChangeKind = 'created' | 'changed' | 'removed';

export interface CostChange {
  id: string;
  /** Woher die Änderung kommt - ein Zugang oder ein sonstiger Kostenposten. */
  source: 'item' | 'fixed';
  itemId: string;
  title: string;
  at: string;
  actorEmail: string;
  kind: CostChangeKind;
  /** Jahreskosten brutto davor und danach. Die Differenz ist die Aussage. */
  beforeAnnualCents: number;
  afterAnnualCents: number;
  /** Was sich geaendert hat, in Worten. */
  note: string;
}

/* ------------------------------------------------------------------------ *
 * Sonstige laufende Kosten
 *
 * Alles, was ein Unternehmen zahlt, ohne dass ein Zugang dahintersteht:
 * Loehne, Steuern, Versicherungen, Miete. Bewusst getrennt von den Zugaengen -
 * hier gibt es nichts zu verschluesseln und nichts freizugeben, dafuer Dinge
 * wie Steuernummern und Kuendigungsfristen.
 * ------------------------------------------------------------------------ */

export type FixedCostStatus = 'active' | 'paused' | 'ended';

export const FIXED_STATUS_LABEL: Record<FixedCostStatus, string> = {
  active: 'Laufend',
  paused: 'Ausgesetzt',
  ended: 'Beendet',
};

/**
 * Vorschlaege, sortiert nach dem, was in einem kleinen Betrieb am meisten
 * ausmacht. Freitext bleibt moeglich - keine Liste trifft jeden Fall.
 */
export const FIXED_CATEGORIES = [
  'Löhne & Gehälter',
  'Sozialabgaben',
  'Lohnsteuer',
  'Gewerbesteuer',
  'Umsatzsteuer-Vorauszahlung',
  'Einkommen- & Körperschaftsteuer',
  'Miete & Nebenkosten',
  'Strom, Gas & Wasser',
  'Internet & Telefon',
  'Betriebshaftpflicht',
  'Inhalts- & Elektronikversicherung',
  'Rechtsschutz',
  'Berufsunfähigkeit & Vorsorge',
  'Krankenversicherung',
  'Steuerberatung',
  'Rechtsberatung',
  'Kammer- & Verbandsbeiträge',
  'Rundfunkbeitrag',
  'Fahrzeug: Leasing',
  'Fahrzeug: Versicherung & Steuer',
  'Kredit & Zinsen',
  'Reinigung & Instandhaltung',
  'Büro & Verpflegung',
  'Personal & Benefits',
  'IT-Dienstleistungen',
  'Marketing & Website',
  'Weiterbildung',
  'Sonstiges',
];

/**
 * Eine Person innerhalb eines Lohnpostens.
 *
 * Bewusst mit Klarnamen statt einer Verknuepfung zum Team: zur Lohnliste
 * gehoeren auch Leute ohne LOCKER-Konto, und eine Verknuepfung wuerde den
 * falschen Eindruck erwecken, die Person koenne ihr Gehalt hier einsehen.
 * Sonstige Kosten sehen ausschliesslich Administratoren.
 */
export interface PayrollPosition {
  id: string;
  name: string;
  /** Rolle oder Stelle, rein zur Einordnung. */
  role: string;
  /** Bruttolohn - das, was auf der Abrechnung oben steht. */
  grossCents: number;
  /**
   * Arbeitgeberanteil: Sozialversicherung, Umlagen, Berufsgenossenschaft.
   * Kommt zum Brutto hinzu und ist genau der Teil, den man beim Rechnen
   * regelmaessig vergisst.
   */
  employerCostCents: number;
  note: string;
}

/**
 * Ueblicher Arbeitgeberanteil in Deutschland, grob gerundet: Kranken-, Pflege-,
 * Renten- und Arbeitslosenversicherung plus Umlagen und Berufsgenossenschaft.
 * Nur ein Startwert - die genaue Zahl steht auf der Lohnabrechnung.
 */
export const EMPLOYER_SHARE_PERCENT = 21;

export interface FixedCost {
  id: string;
  title: string;
  category: string;
  /** An wen es geht: Finanzamt, Versicherung, Vermieter. */
  recipient: string;
  /** Vertrags-, Kunden- oder Steuernummer. */
  reference: string;
  note: string;

  status: FixedCostStatus;
  cycle: BillingCycle;
  amountCents: number;
  currency: string;
  priceIsNet: boolean;
  vatRatePercent: number;
  /** true, wenn der Betrag schwankt - Strom, Löhne mit Überstunden. */
  isVariable: boolean;

  /** Tag im Monat bei monatlicher Zahlung. */
  billingDay: number | null;
  /** Faelligkeit als Anker fuer Quartals- und Jahreszahlungen. */
  dueDate: string | null;
  /** Bei Vertraegen: wann verlaengert sich, bis wann muss gekuendigt werden. */
  renewalDate: string | null;
  cancelBeforeDate: string | null;

  paymentMethod: PaymentMethod;
  paymentDetail: string;
  url: string;
  invoiceUrl: string;

  iconSymbol: string;
  iconData: string;

  ownerUid: string | null;

  /**
   * Aufschluesselung nach Personen. Ist sie gefuellt, ergibt sich `amountCents`
   * aus ihrer Summe und wird nicht mehr von Hand gepflegt.
   */
  positions: PayrollPosition[];

  createdAt: string;
  updatedAt: string;
  updatedBy: string | null;
}

/** Summe aus Brutto und Arbeitgeberanteil - was tatsaechlich vom Konto geht. */
export function payrollTotal(positions: PayrollPosition[]): number {
  return positions.reduce((sum, p) => sum + p.grossCents + p.employerCostCents, 0);
}

/**
 * Die Faelligkeiten der Gewerbesteuer-Vorauszahlung. Als Hinweis im Formular -
 * das vergisst man sonst und wundert sich viermal im Jahr.
 */
export const GEWERBESTEUER_TERMINE = '15.02., 15.05., 15.08. und 15.11.';

export interface AuditEvent {
  id: string;
  at: string;
  actorUid: string;
  actorEmail: string;
  action: string;
  targetId: string | null;
  detail: string;
}

/** Die Hinweise auf der Übersicht, die sich zurückstellen lassen. */
export const HINT_IDS = [
  'duplicates',
  'idleSeats',
  'singleAccess',
  'noOwner',
  'rotation',
  'freeAccess',
] as const;

export type HintId = (typeof HINT_IDS)[number];

/**
 * null bedeutet: dauerhaft ignoriert. Ein Datum bedeutet: bis dahin Ruhe.
 * Zurueckgestellt wird pro Person - was die eine erledigt hat, muss die andere
 * nicht auch noch wegklicken.
 */
export type DismissedHints = Partial<Record<HintId, string | null>>;

export function isHintHidden(dismissed: DismissedHints, id: HintId, now = new Date()): boolean {
  if (!(id in dismissed)) return false;
  const until = dismissed[id];
  if (until === null || until === undefined) return true;
  return new Date(until).getTime() > now.getTime();
}

export interface SessionInfo {
  user: PublicUser;
  keyBundle: VaultKeyBundle | null;
  allowedDomains: string[];
}


/**
 * Der Text, den Mitglieder auf der Startseite sehen.
 *
 * Er steht hier und nicht in der Datenbank, damit auch eine frische
 * Installation etwas Sinnvolles anzeigt. Sobald jemand ihn in den
 * Einstellungen bearbeitet, gewinnt die gespeicherte Fassung.
 *
 * Format: Leerzeile trennt Absaetze, eine Zeile mit "- " ist ein Aufzaehlungs-
 * punkt, eine Zeile mit "# " eine Zwischenueberschrift. Mehr braucht es nicht,
 * und mehr laesst sich auch nicht einschleusen.
 */
export const DEFAULT_WELCOME = `Willkommen bei LOCKER.

Hier liegen die Zugänge des Studios – an einem Ort, verschlüsselt, und für jeden nur das, was er wirklich braucht. Du musst dir kein Passwort mehr merken und keins mehr über Slack oder Zettel weitergeben.

# Was du hier tun kannst
- Unter **Zugänge** findest du alles, was für dich freigeschaltet ist. Passwort kopieren, einloggen, fertig.
- **Persönliche Plätze** – etwa dein eigener Adobe-Zugang – stehen unter dem jeweiligen Eintrag.
- Steht bei einem Eintrag **„Passwort ändern"**, dann bitte beim Anbieter erledigen und hier neu eintragen. Das passiert zum Beispiel, wenn jemand das Studio verlassen hat.
- Brauchst du einen Zugang, den du nicht siehst: kurz Bescheid geben, dann wird er freigegeben.

# Was du wissen solltest
- Deine Master-Passphrase kennt niemand außer dir – auch kein Admin. Geht sie verloren, müssen dir alle Zugänge neu freigegeben werden.
- Nimm für jeden Dienst ein eigenes Passwort. Der Generator im Tool macht das in zwei Klicks.
- Passwörter bitte nicht zusätzlich im Browser speichern. Genau dafür gibt es LOCKER.

Fragen? Meldet euch jederzeit.`;

/** Ein sehr kleiner Satz an Auszeichnungen fuer den Willkommenstext. */
export interface WelcomeBlock {
  kind: 'heading' | 'bullets' | 'paragraph';
  text?: string;
  items?: string[];
}

export function parseWelcome(text: string): WelcomeBlock[] {
  const blocks: WelcomeBlock[] = [];
  let liste: string[] = [];

  const listeAbschliessen = (): void => {
    if (liste.length > 0) {
      blocks.push({ kind: 'bullets', items: liste });
      liste = [];
    }
  };

  for (const zeile of text.replace(/\r/g, '').split('\n')) {
    const t = zeile.trim();
    if (t.startsWith('- ')) {
      liste.push(t.slice(2));
      continue;
    }
    listeAbschliessen();
    if (t.startsWith('# ')) blocks.push({ kind: 'heading', text: t.slice(2) });
    else if (t.length > 0) blocks.push({ kind: 'paragraph', text: t });
  }
  listeAbschliessen();
  return blocks;
}


/** Ein awork-Dokument, so wie es die Auswahl braucht. */
export interface AworkDoc {
  id: string;
  name: string;
  space: string;
  url: string;
  updatedOn: string;
}
